import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useNotificaciones } from "@/hooks/useNotificaciones";
import { formatRelativeTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";

interface NotificationBellProps {
  userId: string;
}

export function NotificationBell({ userId }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const { notificaciones, noLeidas, marcarLeida, marcarTodasLeidas } = useNotificaciones(userId);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-md hover:bg-bg text-fg-secondary"
        aria-label={`Notificaciones (${noLeidas} no leídas)`}
      >
        <Bell className="size-5" />
        {noLeidas > 0 && (
          <span className="absolute top-1 right-1 size-4 bg-error text-white text-[10px] rounded-full grid place-items-center">
            {noLeidas > 9 ? "9+" : noLeidas}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-40 w-80 sm:w-96 card shadow-lg max-h-[70vh] overflow-hidden flex flex-col">
            <header className="flex items-center justify-between p-3 border-b border-border">
              <h3 className="font-semibold">Notificaciones</h3>
              {noLeidas > 0 && (
                <button
                  onClick={marcarTodasLeidas}
                  className="text-xs text-brand hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="size-3" /> Marcar todas
                </button>
              )}
            </header>
            <ul className="flex-1 overflow-y-auto divide-y divide-border">
              {notificaciones.length === 0 ? (
                <li className="p-6 text-center text-fg-muted text-sm">Sin notificaciones</li>
              ) : (
                notificaciones.map((n) => (
                  <li key={n.id}>
                    <Link
                      to={n.link ?? "#"}
                      onClick={() => {
                        if (!n.leida) void marcarLeida(n.id);
                        setOpen(false);
                      }}
                      className={cn(
                        "block p-3 hover:bg-bg transition-colors",
                        !n.leida && "bg-brand/5",
                      )}
                    >
                      <p className="text-sm font-medium">{n.titulo}</p>
                      <p className="text-xs text-fg-secondary line-clamp-2">{n.mensaje}</p>
                      <p className="text-xs text-fg-muted mt-1">
                        {formatRelativeTime(n.created_at)}
                      </p>
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
